import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Box,
  Typography,
  Paper,
  IconButton,
  Tooltip,
  Button,
  TextField,
  ToggleButtonGroup,
  ToggleButton,
  FormControl,
  Stack,
  AppBar,
  Toolbar,
  FormControlLabel,
  Checkbox,
  Slider,
  Select,
  MenuItem,
  InputLabel,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

// Import themes
import { themes } from '../themes';
import { useTheme } from '../context/ThemeContext';

const EmbedPage = () => {
  const navigate = useNavigate();
  const { currentTheme } = useTheme();
  const isIfrcTheme = currentTheme === 'ifrc';
  const [copied, setCopied] = useState(false);
  const [resizeMode, setResizeMode] = useState('fixedHeight');
  const [aspectRatioX, setAspectRatioX] = useState(4);
  const [aspectRatioY, setAspectRatioY] = useState(3.3);
  const [scale, setScale] = useState(0.92);
  const [minHeight, setMinHeight] = useState(710);
  const [eventsCollapsed, setEventsCollapsed] = useState(false);
  const [eventsWidth, setEventsWidth] = useState(25);
  const [showHeader, setShowHeader] = useState(false);
  const [availableViews, setAvailableViews] = useState([]);
  const [defaultView, setDefaultView] = useState('mmr');
  const [selectedTheme, setSelectedTheme] = useState(currentTheme || 'situation-room-default');

  // Update selectedTheme when currentTheme changes
  useEffect(() => {
    if (currentTheme) {
      setSelectedTheme(currentTheme);
    }
  }, [currentTheme]);

  // Fetch available views
  useEffect(() => {
    const fetchViews = async () => {
      try {
        const response = await fetch('/api/views');
        if (!response.ok) throw new Error('Failed to fetch views');
        const data = await response.json();
        // Filter out invisible views
        const visibleViews = data.filter(view => view.is_visible);
        setAvailableViews(visibleViews);
        
        // Set default view to Myanmar if available, otherwise first view
        const myanmar = visibleViews.find(c => c.iso3.toLowerCase() === 'mmr');
        if (myanmar) {
          setDefaultView(myanmar.iso3.toLowerCase());
        } else if (visibleViews.length > 0) {
          setDefaultView(visibleViews[0].iso3.toLowerCase());
        }
      } catch (error) {
        console.error('Error fetching views:', error);
      }
    };

    fetchViews();
  }, []);

  let containerConfig = {
    width: 'auto',
    overflow: 'hidden',
    aspectRatio: `${aspectRatioX}/${aspectRatioY}`,
  };

  let iframeConfig = {
    width: `calc((100%)*${1 / scale})`,
    aspectRatio: `${aspectRatioX}/${aspectRatioY}`,
    transform: `scale(${scale})`,
    transformOrigin: '0 0',
  };

  if (resizeMode === 'fixedHeight') {
    containerConfig = {
      width: 'auto',
      overflow: 'hidden',
      height: minHeight,
    };

    iframeConfig = {
      width: `calc((100%)*${1 / scale})`,
      height: minHeight * (1 / scale),
      transform: `scale(${scale})`,
      transformOrigin: '0 0',
    };
  }

  const getEmbedCode = () => {
    const currentPath = window.location.pathname.replace('/embed', '');
    const baseUrl = import.meta.env.PROD ? window.location.origin : 'http://localhost:3000';
    
    // Add query parameters for events panel configuration
    const queryParams = new URLSearchParams({
      eventsCollapsed: eventsCollapsed.toString(),
      eventsWidth: eventsWidth.toString(),
      showHeader: showHeader.toString(),
      view: defaultView,
      theme: selectedTheme,
    }).toString();
    
    const containerStyle = Object.entries(containerConfig)
      .map(([key, value]) => `${key}: ${value}`)
      .join('; ');
    
    const iframeStyle = Object.entries(iframeConfig)
      .map(([key, value]) => `${key}: ${value}`)
      .join('; ');

    return `<div style="${containerStyle}">
  <iframe 
    src="${baseUrl}/${defaultView}?${queryParams}" 
    frameborder="0" 
    style="${iframeStyle}" 
    allowfullscreen>
  </iframe>
</div>`;
  };

  const handleCopyCode = () => {
    navigator.clipboard.writeText(getEmbedCode());
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const preview = (
    <div style={containerConfig}>
      <iframe 
        frameBorder="0" 
        style={iframeConfig} 
        title="iframe" 
        src={`${window.location.origin}/${defaultView}?${new URLSearchParams({
          eventsCollapsed: eventsCollapsed.toString(),
          eventsWidth: eventsWidth.toString(),
          showHeader: showHeader.toString(),
          theme: selectedTheme,
        }).toString()}`}
      />
    </div>
  );

  const handleBackClick = () => {
    // Get the current view from the URL or default to 'mmr'
    const pathParts = window.location.pathname.split('/');
    const currentView = pathParts.length > 2 ? pathParts[2] : 'mmr';
    
    // Navigate to the main frontend with the current view
    navigate(`/${currentView}`);
  };

  return (
    <>
      <AppBar 
        position="fixed" 
        elevation={0}
        sx={{ 
          bgcolor: isIfrcTheme ? 'var(--color-secondary)' : 'var(--color-primary)',
          zIndex: (theme) => theme.zIndex.drawer + 1,
          '& .MuiToolbar-root': {
            bgcolor: isIfrcTheme ? 'secondary.main' : 'var(--color-primary)'
          }
        }}
      >
        <Toolbar variant="dense" sx={{ minHeight: 52, height: 52 }}>
          <IconButton
            edge="start"
            color="inherit"
            onClick={handleBackClick}
            sx={{ mr: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" sx={{ fontFamily: 'var(--font-family-heading)' }}>
            Embed Dashboard
          </Typography>
        </Toolbar>
      </AppBar>

      <Box sx={{ 
        pt: 8,
        px: 4,
        pb: 4,
        width: '100%',
        mx: 'auto',
      }}>
        {/* Configuration and Embed Code Row */}
        <Box sx={{ 
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 4,
          mb: 6,
          width: '100%',
        }}>
          {/* Configuration Section */}
          <Box sx={{ width: '100%' }}>
            <Typography variant="h6" sx={{ mb: 3, fontFamily: 'var(--font-family-heading)' }}>
              Configuration
            </Typography>

            <Stack spacing={3}>
              <Box>
                <Typography sx={{ mb: 1, color: 'text.secondary', fontSize: '0.875rem' }}>
                  Resize Mode
                </Typography>
                <ToggleButtonGroup
                  value={resizeMode}
                  exclusive
                  onChange={(e, value) => value && setResizeMode(value)}
                  size="small"
                  sx={{ mb: 1 }}
                >
                  <ToggleButton value="fixedHeight">Fixed Height</ToggleButton>
                  <ToggleButton value="aspectRatio">Aspect Ratio</ToggleButton>
                </ToggleButtonGroup>
                <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                  {resizeMode === 'fixedHeight' && 'Fixed height with a dynamic fluid width'}
                  {resizeMode === 'aspectRatio' && 'Dynamic resizing according to a fixed uniform aspect-ratio'}
                </Typography>
              </Box>

              <Box>
                <Typography sx={{ mb: 1, color: 'text.secondary', fontSize: '0.875rem' }}>
                  Zoom Scale
                </Typography>
                <Slider
                  value={scale}
                  onChange={(e, value) => setScale(value)}
                  min={0.1}
                  max={1}
                  step={0.01}
                  marks={[
                    { value: 0.1, label: '10%' },
                    { value: 0.5, label: '50%' },
                    { value: 1, label: '100%' },
                  ]}
                  valueLabelDisplay="auto"
                  valueLabelFormat={(value) => `${Math.round(value * 100)}%`}
                />
              </Box>

              {resizeMode === 'aspectRatio' && (
                <Stack direction="row" spacing={2}>
                  <FormControl fullWidth>
                    <TextField
                      label="Aspect ratio (width)"
                      variant="outlined"
                      value={aspectRatioX}
                      type="number"
                      size="small"
                      inputProps={{
                        step: 0.1,
                        min: 1,
                      }}
                      onChange={(e) => setAspectRatioX(parseFloat(e.target.value))}
                    />
                  </FormControl>
                  <FormControl fullWidth>
                    <TextField
                      label="Aspect ratio (height)"
                      variant="outlined"
                      value={aspectRatioY}
                      type="number"
                      size="small"
                      inputProps={{
                        step: 0.1,
                        min: 1,
                      }}
                      onChange={(e) => setAspectRatioY(parseFloat(e.target.value))}
                    />
                  </FormControl>
                </Stack>
              )}

              {resizeMode === 'fixedHeight' && (
                <FormControl>
                  <TextField
                    label="Height (px)"
                    variant="outlined"
                    value={minHeight}
                    type="number"
                    size="small"
                    inputProps={{
                      step: 10,
                      min: 200,
                    }}
                    onChange={(e) => setMinHeight(parseFloat(e.target.value))}
                  />
                </FormControl>
              )}

              <Box sx={{ borderTop: 1, borderColor: 'divider', pt: 3 }}>
                <Typography sx={{ mb: 2, color: 'text.secondary', fontSize: '0.875rem' }}>
                  Events Panel Settings
                </Typography>
                <Stack spacing={3}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={eventsCollapsed}
                        onChange={(e) => setEventsCollapsed(e.target.checked)}
                      />
                    }
                    label="Collapse events panel by default"
                  />
                  <Box>
                    <Typography sx={{ mb: 1, color: 'text.secondary', fontSize: '0.875rem' }}>
                      Default events panel width (%)
                    </Typography>
                    <Slider
                      value={eventsWidth}
                      onChange={(e, value) => setEventsWidth(value)}
                      min={15}
                      max={40}
                      step={1}
                      marks={[
                        { value: 15, label: '15%' },
                        { value: 25, label: '25%' },
                        { value: 40, label: '40%' },
                      ]}
                      valueLabelDisplay="auto"
                      disabled={eventsCollapsed}
                    />
                  </Box>
                </Stack>
              </Box>
            </Stack>
          </Box>

          {/* Right Column - Embed Code and Display Settings */}
          <Box sx={{ width: '100%' }}>
            {/* Embed Code Section */}
            <Typography variant="h6" sx={{ mb: 3, fontFamily: 'var(--font-family-heading)' }}>
              Embed Code
            </Typography>
            <Paper 
              sx={{ 
                p: 3,
                position: 'relative',
                bgcolor: 'grey.100',
                minHeight: '200px',
                mb: 4,
              }}
            >
              <pre style={{ 
                margin: 0,
                whiteSpace: 'pre-wrap',
                wordWrap: 'break-word',
                fontSize: '14px',
                fontFamily: 'monospace',
                lineHeight: 1.5,
                padding: '8px 0',
              }}>
                {getEmbedCode()}
              </pre>
              <Tooltip title={copied ? "Copied!" : "Copy code"}>
                <IconButton
                  onClick={handleCopyCode}
                  sx={{
                    position: 'absolute',
                    top: 12,
                    right: 12,
                  }}
                >
                  {copied ? <CheckIcon /> : <ContentCopyIcon />}
                </IconButton>
              </Tooltip>
            </Paper>

            {/* Display Settings Section */}
            <Box>
              <Typography variant="h6" sx={{ mb: 3, fontFamily: 'var(--font-family-heading)' }}>
                Display Settings
              </Typography>
              <Stack spacing={3}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={showHeader}
                      onChange={(e) => setShowHeader(e.target.checked)}
                    />
                  }
                  label="Show header"
                />
                
                <FormControl fullWidth size="small">
                  <InputLabel id="view-select-label">Default View</InputLabel>
                  <Select
                    labelId="view-select-label"
                    value={defaultView}
                    label="Default View"
                    onChange={(e) => setDefaultView(e.target.value)}
                  >
                    {availableViews.map((view) => (
                      <MenuItem key={view.iso3} value={view.iso3.toLowerCase()}>
                        {view.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                
                <FormControl fullWidth size="small">
                  <InputLabel id="theme-select-label">Theme</InputLabel>
                  <Select
                    labelId="theme-select-label"
                    value={selectedTheme}
                    label="Theme"
                    onChange={(e) => setSelectedTheme(e.target.value)}
                  >
                    {Object.keys(themes).map((themeKey) => {
                      // Format the theme name with proper capitalization
                      let displayName;
                      switch(themeKey) {
                        case 'immap':
                          displayName = 'iMMAP';
                          break;
                        case 'ifrc':
                          displayName = 'IFRC';
                          break;
                        case 'nrc':
                          displayName = 'NRC';
                          break;
                        case 'united-nations':
                          displayName = 'United Nations';
                          break;
                        case 'situation-room-default':
                          displayName = 'Situation Room Default';
                          break;
                        default:
                          displayName = themeKey.split('-').map(word => 
                            word.charAt(0).toUpperCase() + word.slice(1)
                          ).join(' ');
                      }
                      
                      return (
                        <MenuItem key={themeKey} value={themeKey}>
                          {displayName}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </Stack>
            </Box>
          </Box>
        </Box>

        {/* Preview Row */}
        <Box sx={{ width: '100%' }}>
          <Typography variant="h6" sx={{ mb: 3, fontFamily: 'var(--font-family-heading)' }}>
            Preview
          </Typography>
          <Paper 
            sx={{ 
              p: 0,
              minHeight: '600px',
              overflow: 'hidden',
              bgcolor: 'grey.100',
              border: '1px solid',
              borderColor: 'grey.200',
            }}
          >
            {preview}
          </Paper>
        </Box>
      </Box>
    </>
  );
};

export default EmbedPage; 